import { useEffect, useState, useCallback } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { loginRequest, apiRequest, GROUPS } from '../config/authConfig';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  groups: string[];
}

export interface UseAuthResult {
  user: AuthUser | null;
  isLoading: boolean;
  isAdmin: boolean;
  isHRUser: boolean;
  login: () => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
}

/**
 * Custom hook for authentication
 */
export function useAuth(): UseAuthResult {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [groups, setGroups] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Parse user info from account
  useEffect(() => {
    if (account && inProgress === InteractionStatus.None) {
      const userGroups = (account.idTokenClaims?.groups as string[]) || [];
      setGroups(userGroups);

      setUser({
        id: account.localAccountId || account.homeAccountId,
        email: account.username || '',
        name: account.name || account.username || '',
        groups: userGroups,
      });
      setIsLoading(false);
    } else if (inProgress === InteractionStatus.None && !account) {
      setUser(null);
      setGroups([]);
      setIsLoading(false);
    }
  }, [account, inProgress]);

  // Login function
  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, [instance]);

  // Logout function
  const logout = useCallback(() => {
    void instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance]);

  // Get access token for API calls (uses apiRequest for backend API scope)
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Navigates away; the redirect bridge brings the user back.
        // Concurrent callers may see interaction_in_progress here; never throw.
        try {
          await instance.acquireTokenRedirect({ ...apiRequest, account });
        } catch (redirectError) {
          console.error('Token redirect failed:', redirectError);
        }
        return null;
      }
      console.error('Token acquisition failed:', error);
      return null;
    }
  }, [instance, account]);

  // Check if user is admin
  const isAdmin = groups.includes(GROUPS.ONBOARDING_ADMINS);

  // Check if user is HR (admin or onboarding user)
  const isHRUser = isAdmin || groups.includes(GROUPS.ONBOARDING_USERS);

  return {
    user,
    isLoading: isLoading || inProgress !== InteractionStatus.None,
    isAdmin,
    isHRUser,
    login,
    logout,
    getAccessToken,
  };
}
